import { Suspense, lazy, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Icon } from '../components/icons';
import { Button, ErrorNote, Field, Input, LogoMark } from '../components/ui';
import { useAuth } from '../lib/auth';

// three (the orb) loads only here and in the interview room, never with the dashboard.
const VoiceOrb = lazy(() => import('../components/VoiceOrb'));

const POINTS: Array<[string, string]> = [
  ['Every lead interviewed', 'By phone, web voice or video, in English, Hindi and regional languages.'],
  ['Scored against your rubric', 'Evidence quoted from the transcript; rules applied by code, never by the AI.'],
  ['Your team decides', 'Shortlist, assess with role-plays and tasks, then interview the best yourselves.'],
];

export function LoginPage() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      navigate((location.state as { from?: string } | null)?.from ?? '/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-full lg:grid-cols-[1.1fr_1fr]">
      {/* The brand side: night sky, the orb, what the product does. */}
      <section className="relative hidden overflow-hidden bg-night text-white lg:flex lg:flex-col lg:justify-between lg:p-12">
        <span className="drift pointer-events-none absolute -left-24 top-1/3 h-[480px] w-[480px] rounded-full bg-[#d6217f]/25 blur-3xl" aria-hidden />
        <span className="drift-slow pointer-events-none absolute -right-32 bottom-0 h-[520px] w-[520px] rounded-full bg-[#7e2ea6]/35 blur-3xl" aria-hidden />
        <div className="relative flex items-center gap-3">
          <LogoMark className="h-10 w-10" />
          <span className="leading-tight">
            <span className="display block text-xl font-semibold">Eduvy</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">Interviewer</span>
          </span>
        </div>
        <div className="relative">
          <div className="-ml-12 -mt-10 mb-1 h-[22rem] w-[22rem]">
            <Suspense fallback={<div className="orb m-24 h-40 w-40" aria-hidden />}>
              <VoiceOrb state="ambient" theme="dark" className="h-full w-full" label="AVA, the AI interviewer" />
            </Suspense>
          </div>
          <h2 className="display max-w-lg text-[44px] font-medium leading-[1.05]">
            Interview every lead. <span className="text-brand-gradient">Hire the right ones.</span>
          </h2>
          <ul className="mt-10 max-w-md space-y-5">
            {POINTS.map(([t, d]) => (
              <li key={t} className="flex gap-4">
                <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/15">
                  <Icon name="check" className="h-3.5 w-3.5 text-[#f7a6cf]" strokeWidth={2.5} />
                </span>
                <span>
                  <span className="block font-semibold">{t}</span>
                  <span className="block text-sm text-white/60">{d}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative font-mono text-[11px] uppercase tracking-[0.16em] text-white/40">AI screening · skills assessment · recruiter rounds</p>
      </section>

      <section className="flex items-center justify-center bg-bg bg-brand-wash px-6 py-12">
        <div className="scale-in w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <LogoMark className="h-12 w-12" />
          </div>
          <p className="eyebrow">Welcome back</p>
          <h1 className="display mt-2 text-[34px] font-medium leading-tight text-fg">Sign in to your workspace</h1>
          <p className="mt-2 text-sm text-muted-fg">Your company's jobs, candidates and interviews.</p>
          <form onSubmit={submit} className="surface mt-8 space-y-5 rounded-xl p-6">
            <Field label="Email">
              <Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} leading={<Icon name="mail" className="h-4 w-4" />} placeholder="you@company.com" big />
            </Field>
            <Field label="Password">
              <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} leading={<Icon name="lock" className="h-4 w-4" />} placeholder="••••••••••" big />
            </Field>
            <ErrorNote error={error} />
            <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy} iconRight="arrowRight">
              Sign in
            </Button>
          </form>
          <p className="mt-6 text-center text-xs text-muted-fg">No account? Your company's admin adds you under Settings.</p>
        </div>
      </section>
    </div>
  );
}
